import React from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { 
  Heart, 
  DollarSign, 
  Building, 
  GraduationCap, 
  Palette,
  Users,
  BarChart3,
  Package,
  Megaphone,
  FolderKanban,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Star,
  Play,
  Shield,
  Globe,
  Cloud,
  Database,
  Workflow,
  Brain,
  Target,
  Award,
  Clock,
  TrendingUp,
  Zap,
  Lock,
  HeadphonesIcon,
  Download
} from 'lucide-react';

const ProductDetail = () => {
  const { productName } = useParams();

  const productData = {
    aquacare: {
      name: 'AquaCare',
      tagline: 'Healthcare Management Excellence',
      description: 'Comprehensive healthcare automation platform designed for hospitals, clinics, and healthcare providers to streamline operations and improve patient care.',
      fullDescription: 'AquaCare revolutionizes healthcare management by providing an integrated suite of tools that automate administrative tasks, improve patient outcomes, and ensure regulatory compliance. Built specifically for the healthcare industry, it combines electronic health records, patient management, billing, and telemedicine capabilities in a single, secure platform.',
      icon: Heart,
      color: 'from-red-500 to-pink-500',
      category: 'Healthcare',
      features: [
        'Patient Management System',
        'Electronic Health Records (EHR)',
        'Appointment Scheduling & Reminders',
        'Billing & Insurance Processing',
        'Prescription Management',
        'Medical Inventory Tracking',
        'Telemedicine Integration',
        'HIPAA Compliance & Security',
        'Lab Results Integration',
        'Medical Document Management',
        'Staff Scheduling & Management',
        'Real-time Analytics & Reporting'
      ],
      benefits: [
        'Reduce patient wait times by 60%',
        'Increase staff productivity by 45%',
        'Improve patient satisfaction scores',
        'Ensure 100% HIPAA compliance',
        'Reduce medical errors by 80%',
        'Streamline billing processes'
      ],
      metrics: {
        efficiency: '75% faster patient processing',
        cost: '60% reduction in admin costs',
        satisfaction: '95% patient satisfaction',
        compliance: '100% HIPAA compliant'
      },
      industries: ['Hospitals', 'Clinics', 'Dental Practices', 'Specialty Care', 'Urgent Care', 'Physical Therapy'],
      pricing: {
        starter: '$299/month',
        professional: '$599/month',
        enterprise: 'Custom pricing'
      },
      testimonials: [
        {
          name: '',
          title: '',
          company: 'Metro General Hospital',
          quote: 'AquaCare transformed our patient management completely. We\'ve reduced wait times and improved care quality significantly.'
        }
      ]
    },
    aquainvoice: {
      name: 'AquaInvoice',
      tagline: 'Financial Operations Platform',
      description: 'Advanced financial management and inventory control system for modern businesses seeking operational excellence.',
      fullDescription: 'AquaInvoice is a comprehensive financial management platform that automates invoicing, tracks inventory in real-time, and provides powerful analytics to help businesses make data-driven decisions. Perfect for companies looking to streamline their financial operations and gain complete visibility into their business performance.',
      icon: DollarSign,
      color: 'from-green-500 to-emerald-500',
      category: 'Finance',
      features: [
        'Automated Invoice Generation',
        'Real-time Inventory Management',
        'Financial Reporting & Analytics',
        'Multi-currency Support',
        'Tax Compliance & Automation',
        'Vendor Management',
        'Purchase Order Processing',
        'Cash Flow Forecasting',
        'Payment Gateway Integration',
        'Expense Tracking',
        'Budget Planning & Control',
        'Audit Trail & Compliance'
      ],
      benefits: [
        'Automate 90% of invoicing tasks',
        'Real-time inventory visibility',
        'Reduce financial errors by 95%',
        'Improve cash flow management',
        'Streamline vendor relationships',
        'Ensure tax compliance'
      ],
      metrics: {
        efficiency: '80% faster invoice processing',
        accuracy: '99.9% financial accuracy',
        cost: '50% reduction in processing costs',
        time: '70% less time on admin tasks'
      },
      industries: ['Retail', 'Manufacturing', 'Distribution', 'E-commerce', 'Wholesale', 'Professional Services'],
      pricing: {
        starter: '$199/month',
        professional: '$399/month',
        enterprise: 'Custom pricing'
      },
      testimonials: [
        {
          name: '',
          title: '',
          company: 'TechFlow Solutions',
          quote: 'AquaInvoice gave us complete financial visibility and reduced our invoicing time by 80%. It\'s been a game-changer.'
        }
      ]
    },
    aquabizz: {
      name: 'AquaBizz',
      tagline: 'Enterprise Business Intelligence',
      description: 'Complete business management platform with advanced analytics and automation capabilities for enterprise success.',
      fullDescription: 'AquaBizz is an enterprise-grade business intelligence platform that combines process automation, advanced analytics, and performance management in one comprehensive solution. Designed for large organizations, it provides real-time insights, streamlines workflows, and enables data-driven decision making at scale.',
      icon: Building,
      color: 'from-blue-500 to-cyan-500',
      category: 'Business Intelligence',
      features: [
        'Business Process Automation',
        'Advanced Analytics & BI',
        'Performance Dashboards',
        'Workflow Management',
        'Document Management',
        'Team Collaboration Tools',
        'Project Management',
        'Compliance Monitoring',
        'Real-time Reporting',
        'KPI Tracking & Alerts',
        'Data Integration Hub',
        'Predictive Analytics'
      ],
      benefits: [
        'Increase operational efficiency by 65%',
        'Make decisions 3x faster',
        'Automate routine processes',
        'Improve team collaboration',
        'Ensure regulatory compliance',
        'Drive business growth'
      ],
      metrics: {
        productivity: '65% increase in productivity',
        decisions: '3x faster decision making',
        roi: '300% ROI within 12 months',
        efficiency: '40% operational cost reduction'
      },
      industries: ['Enterprises', 'SMBs', 'Consulting', 'Professional Services', 'Manufacturing', 'Healthcare'],
      pricing: {
        starter: '$499/month',
        professional: '$999/month',
        enterprise: 'Custom pricing'
      },
      testimonials: [
        {
          name: '',
          title: '',
          company: 'Global Dynamics Corp',
          quote: 'AquaBizz transformed how we manage our business processes. The analytics insights have been invaluable for strategic planning.'
        }
      ]
    },
    aqualearn: {
      name: 'AquaLearn',
      tagline: 'Learning Management System',
      description: 'Comprehensive educational platform for corporate training and academic institutions to deliver engaging learning experiences.',
      fullDescription: 'AquaLearn is a modern learning management system that combines traditional course delivery with interactive features, mobile learning, and advanced analytics. Whether for corporate training or academic education, it provides all the tools needed to create, deliver, and track effective learning programs.',
      icon: GraduationCap,
      color: 'from-purple-500 to-indigo-500',
      category: 'Education',
      features: [
        'Course Management System',
        'Virtual Classroom Integration',
        'Assessment & Certification',
        'Progress Tracking & Analytics',
        'Mobile Learning Support',
        'Content Management',
        'Student Information System',
        'Integration with HR Systems',
        'Interactive Learning Tools',
        'Video Conferencing',
        'Gamification Features',
        'Social Learning Platform'
      ],
      benefits: [
        'Improve learning engagement by 85%',
        'Reduce training costs by 70%',
        'Track learning progress in real-time',
        'Create personalized learning paths',
        'Ensure certification compliance',
        'Enable mobile learning'
      ],
      metrics: {
        engagement: '85% learner engagement',
        completion: '92% course completion rate',
        efficiency: '70% training cost reduction',
        satisfaction: '94% learner satisfaction'
      },
      industries: ['Corporations', 'Universities', 'Training Centers', 'Government', 'Healthcare', 'Technology'],
      pricing: {
        starter: '$149/month',
        professional: '$299/month',
        enterprise: 'Custom pricing'
      },
      testimonials: [
        {
          name: '',
          title: '',
          company: 'Innovation Enterprises',
          quote: 'AquaLearn revolutionized our corporate training program. Employee engagement and completion rates have never been higher.'
        }
      ]
    },
    aquastyle: {
      name: 'AquaStyle',
      tagline: 'Creative Project Management',
      description: 'Specialized platform for creative agencies, design studios, and marketing teams to manage projects and collaborate effectively.',
      fullDescription: 'AquaStyle is built specifically for creative professionals, combining project management with asset management, client collaboration, and creative workflow automation. It understands the unique needs of creative teams and provides tools to streamline the creative process from concept to delivery.',
      icon: Palette,
      color: 'from-pink-500 to-rose-500',
      category: 'Creative',
      features: [
        'Project Portfolio Management',
        'Creative Asset Management',
        'Client Collaboration Tools',
        'Time Tracking & Billing',
        'Brand Guidelines Management',
        'Approval Workflows',
        'Resource Planning',
        'Creative Analytics',
        'Design Version Control',
        'Client Feedback System',
        'Campaign Management',
        'Creative Brief Templates'
      ],
      benefits: [
        'Deliver projects 40% faster',
        'Improve client satisfaction',
        'Streamline approval processes',
        'Better resource utilization',
        'Maintain brand consistency',
        'Increase team productivity'
      ],
      metrics: {
        delivery: '40% faster project delivery',
        quality: '90% first-time approval rate',
        utilization: '85% resource utilization',
        satisfaction: '96% client satisfaction'
      },
      industries: ['Agencies', 'Design Studios', 'Marketing Teams', 'Media Companies', 'Freelancers', 'Consultancies'],
      pricing: {
        starter: '$179/month',
        professional: '$349/month',
        enterprise: 'Custom pricing'
      },
      testimonials: [
        {
          name: '',
          title: '',
          company: 'Pixel Perfect Agency',
          quote: 'AquaStyle streamlined our entire creative workflow. We\'re delivering higher quality work faster than ever before.'
        }
      ]
    }
  };

  const product = productData[productName?.toLowerCase()];

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <p className="text-xl text-gray-600 mb-8">The product you're looking for doesn't exist.</p>
          <Link to="/products" className="btn-primary">
            ← Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = product.icon;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className={`relative bg-gradient-to-br ${product.color} py-20 lg:py-32`}>
        <div className="absolute inset-0 bg-black/20"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Link 
                to="/products" 
                className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Products
              </Link>

              <div className="flex items-center justify-center mb-6">
                <div className="w-20 h-20 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <IconComponent className="h-10 w-10 text-white" />
                </div>
              </div>

              <h1 className="text-5xl lg:text-7xl font-bold mb-6">
                {product.name}
              </h1>
              
              <p className="text-2xl lg:text-3xl font-semibold mb-8 text-white/90">
                {product.tagline}
              </p>
              
              <p className="text-xl lg:text-2xl text-white/80 mb-12 leading-relaxed max-w-3xl mx-auto">
                {product.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="https://app.aquargin.com/register"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </motion.a>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Watch Demo
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {Object.entries(product.metrics).map(([key, value], index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow"
              >
                <div className={`text-3xl lg:text-4xl font-bold bg-gradient-to-r ${product.color} bg-clip-text text-transparent mb-2`}>
                  {value}
                </div>
                <div className="text-sm text-gray-600 capitalize font-medium">{key.replace(/([A-Z])/g, ' $1')}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Overview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                  Why Choose {product.name}?
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  {product.fullDescription}
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">Key Benefits:</h3>
                <div className="grid grid-cols-1 gap-3">
                  {product.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className={`bg-gradient-to-br ${product.color} rounded-2xl p-8 text-white`}>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-2xl font-bold">{product.name} Dashboard</h4>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium">Live Demo</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
                      <div className="text-3xl font-bold mb-1">98.7%</div>
                      <div className="text-sm opacity-90">System Uptime</div>
                    </div>
                    
                    <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
                      <div className="text-3xl font-bold mb-1">24/7</div>
                      <div className="text-sm opacity-90">Support Available</div>
                    </div>
                  </div>
                  
                  <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium">Performance Score</span>
                      <span className="text-green-300 font-bold">Excellent</span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-3">
                      <div className="bg-green-400 h-3 rounded-full animate-pulse" style={{ width: '94%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Complete Feature Set
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to transform your {product.category.toLowerCase()} operations in one comprehensive platform.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {product.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-gray-50 p-6 rounded-xl hover:shadow-lg hover:bg-white transition-all duration-300 group"
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${product.color}`}></div>
                  <span className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                    {feature}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Trusted by Industries
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {product.name} serves diverse industries with specialized solutions for each sector's unique needs.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {product.industries.map((industry, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`px-6 py-3 bg-gradient-to-r ${product.color} text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-shadow`}
              >
                {industry}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="flex justify-center">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-8 w-8 text-yellow-400 fill-current" />
                ))}
              </div>
              
              <blockquote className="text-2xl lg:text-3xl font-medium text-gray-900 italic leading-relaxed">
                "{product.testimonials[0].quote}"
              </blockquote>
              
              <div className="flex items-center justify-center space-x-4">
                <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${product.color} flex items-center justify-center text-white font-bold text-xl`}>
                  {product.testimonials[0].name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="text-left">
                  <div className="font-bold text-gray-900 text-lg">{product.testimonials[0].name}</div>
                  <div className="text-gray-600">{product.testimonials[0].title}</div>
                  <div className="text-sm text-gray-500">{product.testimonials[0].company}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Simple, Transparent Pricing
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the plan that fits your organization's needs. Start with a free trial, no commitment required.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {Object.entries(product.pricing).map(([plan, price], index) => (
              <motion.div
                key={plan}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 ${
                  plan === 'professional' ? `border-4 border-gradient-to-r ${product.color}` : 'border border-gray-200'
                }`}
              >
                <div className="text-center space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 capitalize mb-2">{plan}</h3>
                    <div className="text-4xl font-bold text-gray-900">
                      {price === 'Custom pricing' ? (
                        <span className="text-2xl">Custom</span>
                      ) : (
                        <>
                          <span className="text-lg text-gray-500">$</span>
                          {price.replace('$', '').replace('/month', '')}
                          <span className="text-lg text-gray-500">/mo</span>
                        </>
                      )}
                    </div>
                  </div>

                  <a
                    href={plan === 'enterprise' ? '/contact' : 'https://app.aquargin.com/register'}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 block text-center ${
                      plan === 'professional'
                        ? `bg-gradient-to-r ${product.color} text-white hover:shadow-lg`
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {plan === 'enterprise' ? 'Contact Sales' : 'Start Free Trial'}
                  </a>

                  <div className="text-sm text-gray-600">
                    {plan === 'starter' && 'Perfect for small teams'}
                    {plan === 'professional' && 'Most popular choice'}
                    {plan === 'enterprise' && 'For large organizations'}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-20 bg-gradient-to-r ${product.color}`}>
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Ready to Transform Your {product.category}?
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Join thousands of organizations already using {product.name} to streamline operations and drive growth.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Start 30-Day Free Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <HeadphonesIcon className="mr-2 h-5 w-5" />
                Schedule Demo
              </motion.button>
            </div>

            <div className="mt-8 text-white/80 text-sm">
              <p>✓ No credit card required  ✓ Full feature access  ✓ 24/7 support included</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail; 